import { api, unwrap, type ResponseEnvelope } from '@saas-kit/lib';

import type {
  ListEnvelope,
  QmsCategory,
  QmsCounter,
  QmsCounterGroup,
  QmsCustomerType,
  QmsService,
} from './types';

/** A staff member who can be assigned to a counter. */
export interface QmsAssignableStaff {
  user_id: string;
  label: string;
}

// --- Categories -----------------------------------------------------------

export async function listCategories(): Promise<QmsCategory[]> {
  const data = await unwrap<ListEnvelope<QmsCategory>>(
    api.get('/qms/categories'),
  );
  return data.items;
}

export async function createCategory(input: {
  code: string;
  name: string;
}): Promise<QmsCategory> {
  return unwrap<QmsCategory>(api.post('/qms/categories', input));
}

export async function updateCategory(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsCategory> {
  return unwrap<QmsCategory>(api.patch(`/qms/categories/${id}`, input));
}

export async function deleteCategory(id: string): Promise<void> {
  await unwrap(api.delete<ResponseEnvelope<unknown>>(`/qms/categories/${id}`));
}

// --- Customer types -------------------------------------------------------

export async function listCustomerTypes(): Promise<QmsCustomerType[]> {
  const data = await unwrap<ListEnvelope<QmsCustomerType>>(
    api.get('/qms/customer-types'),
  );
  return data.items;
}

export async function createCustomerType(input: {
  code: string;
  name: string;
  rank: number;
  is_default: boolean;
}): Promise<QmsCustomerType> {
  return unwrap<QmsCustomerType>(api.post('/qms/customer-types', input));
}

export async function updateCustomerType(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsCustomerType> {
  return unwrap<QmsCustomerType>(api.patch(`/qms/customer-types/${id}`, input));
}

export async function deleteCustomerType(id: string): Promise<void> {
  await unwrap(
    api.delete<ResponseEnvelope<unknown>>(`/qms/customer-types/${id}`),
  );
}

// --- Services -------------------------------------------------------------

export async function listServices(): Promise<QmsService[]> {
  const data = await unwrap<ListEnvelope<QmsService>>(api.get('/qms/services'));
  return data.items;
}

export async function createService(input: {
  code: string;
  name: string;
  name_ar?: string | null;
  category_id?: string | null;
  token_prefix?: string | null;
  daily_cap?: number | null;
}): Promise<QmsService> {
  return unwrap<QmsService>(api.post('/qms/services', input));
}

export async function updateService(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsService> {
  return unwrap<QmsService>(api.patch(`/qms/services/${id}`, input));
}

export async function deleteService(id: string): Promise<void> {
  await unwrap(api.delete<ResponseEnvelope<unknown>>(`/qms/services/${id}`));
}

// --- Counter groups -------------------------------------------------------

export async function listCounterGroups(): Promise<QmsCounterGroup[]> {
  const data = await unwrap<ListEnvelope<QmsCounterGroup>>(
    api.get('/qms/counter-groups'),
  );
  return data.items;
}

export async function createCounterGroup(input: {
  code: string;
  name: string;
}): Promise<QmsCounterGroup> {
  return unwrap<QmsCounterGroup>(api.post('/qms/counter-groups', input));
}

export async function updateCounterGroup(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsCounterGroup> {
  return unwrap<QmsCounterGroup>(
    api.patch(`/qms/counter-groups/${id}`, input),
  );
}

export async function deleteCounterGroup(id: string): Promise<void> {
  await unwrap(
    api.delete<ResponseEnvelope<unknown>>(`/qms/counter-groups/${id}`),
  );
}

// --- Counters -------------------------------------------------------------

export async function listCounters(): Promise<QmsCounter[]> {
  const data = await unwrap<ListEnvelope<QmsCounter>>(api.get('/qms/counters'));
  return data.items;
}

export async function createCounter(input: {
  code: string;
  name: string;
  group_id?: string | null;
  service_ids: string[];
}): Promise<QmsCounter> {
  return unwrap<QmsCounter>(api.post('/qms/counters', input));
}

export async function updateCounter(
  id: string,
  input: Record<string, unknown>,
): Promise<QmsCounter> {
  return unwrap<QmsCounter>(api.patch(`/qms/counters/${id}`, input));
}

export async function setCounterServices(
  id: string,
  serviceIds: string[],
): Promise<QmsCounter> {
  return unwrap<QmsCounter>(
    api.post(`/qms/counters/${id}/services`, { service_ids: serviceIds }),
  );
}

/** Counters the current user may claim (assigned ones, or all for managers). */
export async function listClaimableCounters(): Promise<QmsCounter[]> {
  const data = await unwrap<ListEnvelope<QmsCounter>>(
    api.get('/qms/counters/claimable'),
  );
  return data.items;
}

/** Replace the full set of staff users allowed to claim a counter. */
export async function setCounterAssignments(
  id: string,
  userIds: string[],
): Promise<QmsCounter> {
  return unwrap<QmsCounter>(
    api.post(`/qms/counters/${id}/assignments`, { user_ids: userIds }),
  );
}

/** Active tenant members, shaped as counter-assignment options. */
export async function listAssignableStaff(): Promise<QmsAssignableStaff[]> {
  const data = await unwrap<
    ListEnvelope<{ user_id: string; full_name: string | null; email: string }>
  >(api.get('/memberships'));
  return data.items.map((m) => ({
    user_id: m.user_id,
    label: m.full_name?.trim() || m.email,
  }));
}

export async function deleteCounter(id: string): Promise<void> {
  await unwrap(api.delete<ResponseEnvelope<unknown>>(`/qms/counters/${id}`));
}
